import React from "react";
import { Chip } from "@mui/material";

type PreventivoStatus = "open" | "processing" | "ready" | "error" | "closed";
type MatchType = "codice" | "exact" | "acceptable" | "excluded" | "manual" | "campione";

const PREVENTIVO_COLORS: Record<PreventivoStatus, { bg: string; color: string; label: string }> = {
  open:       { bg: "#E3F2FD", color: "#1565C0", label: "Aperto" },
  processing: { bg: "#FFF3E0", color: "#E65100", label: "Elaborazione..." },
  ready:      { bg: "#E8F5E9", color: "#2E7D32", label: "Pronto" },
  error:      { bg: "#FFEBEE", color: "#C62828", label: "Errore" },
  closed:     { bg: "#F5F5F5", color: "#616161", label: "Chiuso" },
};

const MATCH_COLORS: Record<MatchType, { bg: string; color: string; label: string }> = {
  codice:     { bg: "#E8EAF6", color: "#283593", label: "Codice" },
  exact:      { bg: "#E8F5E9", color: "#2E7D32", label: "Esatto" },
  acceptable: { bg: "#FFFDE7", color: "#F57F17", label: "Accettabile" },
  excluded:   { bg: "#FAFAFA", color: "#9E9E9E", label: "Escluso" },
  manual:     { bg: "#F3E5F5", color: "#6A1B9A", label: "Manuale" },
  campione:   { bg: "#E0F7FA", color: "#00695C", label: "Campione" },
};

export const PreventivoStatusChip: React.FC<{
  status: PreventivoStatus | string;
  size?: "small" | "medium";
}> = ({ status, size = "small" }) => {
  const cfg = PREVENTIVO_COLORS[status as PreventivoStatus] ?? {
    bg: "#F5F5F5", color: "#616161", label: status,
  };
  return (
    <Chip
      label={cfg.label}
      size={size}
      sx={{ bgcolor: cfg.bg, color: cfg.color, fontWeight: 600, fontSize: "0.72rem" }}
    />
  );
};

export const MatchTypeChip: React.FC<{
  matchType?: string | null;
  size?: "small" | "medium";
  matchPercent?: number | null;
  /** When true, overrides the displayed label to "Manuale" to signal a user
   *  forced-selection, while the underlying match_type remains unchanged so
   *  that the original pipeline classification is still available elsewhere. */
  manuallySelected?: boolean;
  /** Smaller chip for dense tables. */
  dense?: boolean;
}> = ({ matchType, size = "small", matchPercent, manuallySelected, dense }) => {
  if (!matchType && !manuallySelected) {
    return null;
  }
  const cfg = manuallySelected
    ? MATCH_COLORS["manual"]
    : (MATCH_COLORS[matchType as MatchType] ?? { bg: "#F5F5F5", color: "#9E9E9E", label: matchType ?? "" });
  const label = matchPercent != null
    ? `${cfg.label} · ${matchPercent.toFixed(0)}%`
    : cfg.label;
  return (
    <Chip
      label={label}
      size={size}
      sx={{
        bgcolor: cfg.bg,
        color: cfg.color,
        fontWeight: 600,
        fontSize: dense ? "0.64rem" : "0.72rem",
        ...(dense ? { height: 18, "& .MuiChip-label": { px: 0.75 } } : {}),
      }}
    />
  );
};

export const DisciplineChip: React.FC<{
  disciplina?: string | null;
  size?: "small" | "medium";
}> = ({ disciplina, size = "small" }) => {
  if (!disciplina) return null;
  const isEle = disciplina === "ELE";
  return (
    <Chip
      label={disciplina}
      size={size}
      sx={{
        bgcolor: isEle ? "#E3F2FD" : "#FFF3E0",
        color: isEle ? "#1565C0" : "#E65100",
        fontWeight: 700,
        fontSize: "0.68rem",
      }}
    />
  );
};

export const CategoryChip: React.FC<{
  categoria?: string | null;
  size?: "small" | "medium";
}> = ({ categoria, size = "small" }) => {
  if (!categoria) return null;
  return (
    <Chip
      label={categoria}
      size={size}
      sx={{
        bgcolor: "#F3E5F5",
        color: "#6A1B9A",
        fontWeight: 600,
        fontSize: "0.68rem",
      }}
    />
  );
};
